/**
 * Лігво Змія Горинича: a hall cut into a mountain, with a way in.
 *
 * "Живе в горах або в печерах. Його лігво зазвичай захищене скарбами."
 *
 * One to every thousand blocks square, which Pavlo settled after the first
 * answer - "so само часто як замок" - turned out to mean one in the whole
 * world, and one dragon in a four-thousand-block country is a dragon nobody
 * meets.
 *
 * Written out of the seed like a село and a скарб, column by column, for the
 * reason both of those are: «Пригода» throws away country nobody is near and
 * grows it again, and a hall carved once is a hall that comes back solid.
 * Every shape here is a function of the lair's own position, so the column
 * holding the mouth and the column holding the far wall agree without either
 * having been grown.
 */

/** How much country one dragon has to itself. */
export const LAIR_CELL = 1000;

/** How far from the middle of a lair anything of it reaches. */
export const LAIR_REACH = 40;

/** The hall: half-axes across, up and along, in blocks. */
export const HALL = { x: 9, y: 6, z: 11 } as const;

/** How far under the mountain's surface the middle of the hall sits. */
export const HALL_DEPTH = 13;

/** How many places in a district are tried before it is given up on. */
export const LAIR_TRIES = 36;

function hash(a: number, b: number, c: number): number {
  let h = (a * 374761393 + b * 668265263 + c * 2246822519) >>> 0;
  h = (h ^ (h >>> 13)) >>> 0;
  h = (h * 1274126177) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/**
 * Every place in this district a lair might be cut, in the order to try.
 *
 * The same shape as `villageTries`, and for the same reason it exists: a
 * district has exactly one of these, and asking one point whether it happens
 * to be a mountain gets an answer of "no" nearly every time. Thirty-six
 * places, each jittered inside its own share of the district so the result
 * is not a grid, and the order shuffled by the seed.
 */
export function lairTries(
  seed: number, gx: number, gz: number,
): { x: number; z: number }[] {
  const edge = LAIR_REACH + 60;
  const room = LAIR_CELL - 2 * edge;
  const side = Math.round(Math.sqrt(LAIR_TRIES));
  const cell = room / side;
  const out: { x: number; z: number; order: number }[] = [];
  for (let i = 0; i < side * side; i++) {
    const cx = i % side, cz = Math.floor(i / side);
    const jx = hash(gx * 149 + i, gz, seed + 401);
    const jz = hash(gx, gz * 149 + i, seed + 419);
    out.push({
      x: Math.round(gx * LAIR_CELL + edge + (cx + jx) * cell),
      z: Math.round(gz * LAIR_CELL + edge + (cz + jz) * cell),
      order: hash(gx * 6151 + i, gz * 24593, seed + 431),
    });
  }
  out.sort((a, b) => a.order - b.order);
  return out.map(({ x, z }) => ({ x, z }));
}

/** Which way the ground falls away from a lair: the mouth opens that way. */
export type Way = { readonly dx: -1 | 0 | 1; readonly dz: -1 | 0 | 1 };
export const WAYS: readonly Way[] = [
  { dx: 0, dz: -1 }, { dx: 1, dz: 0 }, { dx: 0, dz: 1 }, { dx: -1, dz: 0 },
];

/** Is this cell inside the hall, given its middle? */
export function inHall(
  dx: number, dy: number, dz: number,
): boolean {
  const a = dx / HALL.x, b = dy / HALL.y, c = dz / HALL.z;
  return a * a + b * b + c * c <= 1;
}

/**
 * Is this cell inside the tunnel out of it?
 *
 * A square bore rather than a round one: a passage a dragon walks out of,
 * and the thing that makes it read as cut rather than as a worm hole. `t` is
 * how far along the tunnel the cell is, which the caller works out because
 * it is the caller that knows which way the ground falls.
 */
export const BORE = { wide: 3, tall: 4 } as const;
